import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@shared/ui/dialog";
import { Button } from "@shared/ui/button";
import { Input } from "@shared/ui/input";
import { Label } from "@shared/ui/label";
import { Plus, Trash, Edit, Save, X } from "lucide-react";
import { Collapsible } from "@shared/ui/collapsible";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import {
  useDeleteValidationCode,
  usePostValidationCode,
  usePutValidationCode,
  useValidationCodes,
} from "../../hooks";
import { logEvent } from "@shared/analytics/analytics";

interface ExemptionCodesDialogProps {
  open: boolean;
  onClose: () => void;
}

export const ExemptionCodesDialog = ({
  open,
  onClose,
}: ExemptionCodesDialogProps) => {
  const remoteUtilityId = useRemoteUtilityId();
  const { data: validationCodes } = useValidationCodes(remoteUtilityId);
  const putValidationCodeMutation = usePutValidationCode();
  const deleteValidationCodeMutation = useDeleteValidationCode();
  const addCode = usePostValidationCode();

  const [newCode, setNewCode] = useState({
    code: "",
    description: "",
  });

  const [editingCodeId, setEditingCodeId] = useState<number | null>(null);
  const [editingCodeValues, setEditingCodeValues] = useState({
    code: "",
    name: "",
  });

  const [isAddingCode, setIsAddingCode] = useState(false);

  const handleAddCode = () => {
    addCode.mutate({
      code: newCode.code,
      name: newCode.description,
      remote_utility_id: remoteUtilityId,
    }, {
      onSuccess: () => {
        logEvent("Exemption Code added", {
          code: newCode.code,
          name: newCode.description,
        })
      }
    });
    setNewCode({
      code: "",
      description: ""
    })
  };

  const handleStartEdit = (code: any) => {
    setEditingCodeId(code.id);
    setEditingCodeValues({
      code: code.code,
      name: code.name,
    });
  };

  const handleSaveEdit = (codeId: number) => {
    putValidationCodeMutation.mutate({
      id: codeId,
      remoteUtilityId,
      payload: {
        name: editingCodeValues.name,
        code: editingCodeValues.code,
      },
    }, {
      onSuccess: () => {
        logEvent("Exemption Code Edited", {
          code: newCode.code,
          name: newCode.description,
        })
      }
    });
    setEditingCodeId(null);
  };

  const handleCancelEdit = () => {
    setEditingCodeId(null);
  };

  const handleDeleteCode = (codeId: number) => {
    // You would typically call a DELETE API here (not included)
    deleteValidationCodeMutation.mutate({
    id: codeId,
    remoteUtilityId,
});
  };

  const handleSave = () => {
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[700px] lg:max-w-[800px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Exemption Codes</DialogTitle>
          <DialogDescription>Configure exemption codes and remarks options.</DialogDescription>
        </DialogHeader>

        <div className="mt-6 space-y-4">
          {/* Add new code */}
          {!isAddingCode ? (
            <Button
              className="w-full"
              variant="outline"
              onClick={() => setIsAddingCode(true)}
            >
              <Plus className="h-4 w-4 mr-2" /> Add New Validation Code
            </Button>
          ) : (
            <div className="border rounded-md p-4">
              <h3 className="font-medium mb-2">Add New Validation Code</h3>
              <div className="grid gap-3">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label htmlFor="new-code">Code</Label>
                    <Input
                      id="new-code"
                      placeholder="e.g. NI"
                      value={newCode.code}
                      onChange={(e) =>
                        setNewCode((prev) => ({
                          ...prev,
                          code: e.target.value,
                        }))
                      }
                      maxLength={5}
                    />
                  </div>
                  <div>
                    <Label htmlFor="new-description">Description</Label>
                    <Input
                      id="new-description"
                      placeholder="e.g. Not Inspected"
                      value={newCode.description}
                      onChange={(e) =>
                        setNewCode((prev) => ({
                          ...prev,
                          description: e.target.value,
                        }))
                      }
                    />
                  </div>
                </div>
                <div className="flex justify-end space-x-2 mt-2">
                  <Button
                    variant="ghost"
                    onClick={() => setIsAddingCode(false)}
                  >
                    Cancel
                  </Button>
                  <Button onClick={handleAddCode}>Add Code</Button>
                </div>
              </div>
            </div>
          )}

          {/* Existing validation codes */}
          {validationCodes?.map((validationCode) => (
            <Collapsible key={validationCode.id} className="border rounded-md">
              <div className="p-4">
                {editingCodeId === validationCode.id ? (
                  <div className="mb-3">
                    <div className="grid grid-cols-2 gap-2 mb-2">
                      <Input
                        placeholder="Code"
                        value={editingCodeValues.code}
                        onChange={(e) =>
                          setEditingCodeValues((prev) => ({
                            ...prev,
                            code: e.target.value,
                          }))
                        }
                        maxLength={5}
                      />
                      <Input
                        placeholder="Name"
                        value={editingCodeValues.name}
                        onChange={(e) =>
                          setEditingCodeValues((prev) => ({
                            ...prev,
                            name: e.target.value,
                          }))
                        }
                      />
                    </div>
                    <div className="flex justify-end space-x-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={handleCancelEdit}
                      >
                        <X className="h-4 w-4 mr-1" /> Cancel
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleSaveEdit(validationCode.id)}
                      >
                        <Save className="h-4 w-4 mr-1" /> Save
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <div className="flex items-center">
                        <span className="bg-blue-100 text-blue-800 text-xs font-medium mr-2 px-2.5 py-0.5 rounded">
                          {validationCode.code}
                        </span>
                        <h3 className="font-medium">{validationCode.name}</h3>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleStartEdit(validationCode)}
                      >
                        <Edit className="h-4 w-4" />
                        <span className="sr-only">Edit</span>
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-destructive"
                        onClick={() => handleDeleteCode(validationCode.id)}
                      >
                        <Trash className="h-4 w-4" />
                        <span className="sr-only">Delete</span>
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </Collapsible>
          ))}
        </div>

        <DialogFooter className="mt-6">
          <Button variant="outline" onClick={onClose} className="mr-2">
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
